/**
 * 「從媒體庫資料夾套用」——一次把整個景點的素材、文字與推薦點歸位。
 *
 * 逐格掛載要開 12 次挑選器、切 3 次語言頁籤，而且每次都得在上百筆素材裡認出正確
 * 的檔；推薦點更是 20 筆卡片各自 9 個欄位。但窗屏規格早就把檔名訂死成欄位名
 * （`1_pic1.png`、`5_2_str4.txt` …），這些判斷沒有一個需要人來做。
 *
 * **對應規則不在這裡**。這個元件不認得任何檔名規則，它只把後端算好的計畫畫出來
 * （見 `spot-folder-import.rules.ts` 與 `spot-poi.rules.ts`）。規則同時放前後端
 * 遲早會漂移成兩套，而漂移的症狀是船上播錯內容，兩邊都不會報錯。
 *
 * 一律先預覽再套用：滿載時這個動作一次動 27 個景點層欄位加 20 筆推薦點，看不到
 * 會發生什麼就按下去太危險。
 */

import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from './Button';
import { DISPLAY_FIELDS } from './SpotDisplayFields';
import { SpotFolderIssues } from './SpotFolderIssues';
import { SpotFolderUpload } from './SpotFolderUpload';
import { SLOTS } from './SpotMediaFields';
import { applySpotFolder, type SpotFolderPlan } from '../lib/api-client';

const LOCALE_LABEL: Record<string, string> = { zh: '中', en: '英', jp: '日' };

const SLOT_LABEL: Record<string, string> = Object.fromEntries(SLOTS.map((s) => [s.slot, s.label]));

/** 計畫回傳的是 `poi_title_i18n`，欄位規格用的是 `poi_title`。 */
const FIELD_LABEL: Record<string, string> = Object.fromEntries(
  DISPLAY_FIELDS.map((f) => [`${f.key}_i18n`, f.label]),
);

const POI_CATEGORY_SHORT: Record<string, string> = {
  food: '美食',
  drinks: '飲品',
  ent: '娛樂',
  shopping: '購物',
};

interface Props {
  spotId: string;
  /** 景點的窗屏編號；資料夾預設值。空字串代表尚未設定。 */
  roiId: string;
  /** 套用成功後呼叫，供頁面重新載入景點與插槽。 */
  onApplied: () => void;
}

export function SpotFolderApply({ spotId, roiId, onApplied }: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  /**
   * null = 使用者還沒改過，跟著景點的窗屏編號走。
   *
   * 不能用 `useState(roiId)`：景點資料是非同步載入的，首次 render 時 `roiId`
   * 還是空字串，而 useState 的初始值只取一次——結果是設了編號的景點反而拿到
   * 空白預設值。
   */
  const [folderOverride, setFolderOverride] = useState<string | null>(null);
  const folder = folderOverride ?? roiId;
  const setFolder = setFolderOverride;
  const [overwrite, setOverwrite] = useState(false);
  const [plan, setPlan] = useState<SpotFolderPlan | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const preview = useMutation({
    mutationFn: (v: { folder: string; overwrite: boolean }) =>
      applySpotFolder(spotId, v.folder, { dryRun: true, overwrite: v.overwrite }),
    onSuccess: (p) => {
      setPlan(p);
      setDone(null);
    },
  });

  const apply = useMutation({
    mutationFn: () => applySpotFolder(spotId, folder, { dryRun: false, overwrite }),
    onSuccess: (p) => {
      const slots = p.media.filter((m) => m.action !== 'skip_existing').length;
      const fields = p.text.filter((t) => t.action !== 'skip_existing').length;
      const pois = p.pois.filter((x) => x.action !== 'skip_existing').length;
      setDone(`已套用 ${slots} 個插槽、${fields} 個文字欄位、${pois} 筆推薦點。`);
      setPlan(p);
      onApplied();
    },
  });

  const busy = preview.isPending || apply.isPending;
  const error = (preview.error ?? apply.error) as Error | null;

  /** 改條件後舊計畫就不再成立，留著會讓人以為按「套用」會照它執行。 */
  const invalidatePlan = (): void => {
    setPlan(null);
    setDone(null);
  };

  /**
   * 手動指派之後重算計畫。
   *
   * 指派已經寫進資料庫了，舊計畫上那一列卻還停在「未套用」——不重算的話，使用者
   * 會對著一份與現況不符的清單繼續操作。順便讓景點表單重新載入（文字指派會改欄位）。
   */
  const afterAssign = (): void => {
    onApplied();
    preview.mutate({ folder, overwrite });
  };

  if (!open) {
    return (
      <div style={{ margin: '10px 0' }}>
        <Button variant="secondary" onClick={() => setOpen(true)}>
          📂 匯入整包素材…
        </Button>
        <small style={{ color: '#9ca3af', marginLeft: 8 }}>
          把資料夾拖進來就好——上傳、依檔名歸位、三語系的圖片語音影片文字與推薦點一次到位
        </small>
      </div>
    );
  }

  return (
    <div
      style={{
        border: '1px solid #bfdbfe',
        background: '#f8fbff',
        borderRadius: 6,
        padding: 12,
        margin: '10px 0',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13 }}>匯入整包素材</strong>
        <input
          type="text"
          value={folder}
          onChange={(e) => {
            setFolder(e.target.value);
            invalidatePlan();
          }}
          placeholder="媒體庫資料夾，例如 0"
          style={{ padding: 5, width: 180 }}
        />
        <Button
          variant="primary"
          onClick={() => preview.mutate({ folder, overwrite })}
          disabled={busy || folder.trim() === ''}
        >
          {preview.isPending ? '讀取中…' : '預覽'}
        </Button>
        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
          <input
            type="checkbox"
            checked={overwrite}
            onChange={(e) => {
              setOverwrite(e.target.checked);
              invalidatePlan();
            }}
          />
          連同已有內容一起覆蓋
        </label>
        <span style={{ flex: 1 }} />
        <Button
          variant="ghost"
          onClick={() => {
            setOpen(false);
            invalidatePlan();
          }}
        >
          關閉
        </Button>
      </div>

      {/*
        拖放區擺在條件列之下、計畫之上，順序就是使用者的動線：確認資料夾 → 丟檔案
        → 看計畫 → 套用。它上傳完會自己觸發預覽，所以「預覽」按鈕只在素材早就在
        媒體庫裡時才需要按。
      */}
      <SpotFolderUpload folder={folder} onUploaded={() => preview.mutate({ folder, overwrite })} />

      <p style={{ fontSize: 12, color: '#6b7280', margin: '8px 0 0' }}>
        景點層掃 <code>{folder || 'N'}/zh</code>、<code>{folder || 'N'}/en</code>、
        <code>{folder || 'N'}/jp</code> 這一層；推薦點另掃其下的 <code>4_美食</code> /{' '}
        <code>5_美食</code> 等 8 個子資料夾。兩層各歸各的， 推薦點的素材不會被當成景點插槽。
        {roiId === '' && '　此景點尚未設定窗屏編號，請手動輸入資料夾名稱。'}
      </p>

      {error && (
        <div className="error-banner" style={{ marginTop: 8 }}>
          {error.message}
        </div>
      )}
      {done && (
        <div
          style={{
            marginTop: 8,
            padding: 8,
            fontSize: 12,
            background: '#ecfdf5',
            border: '1px solid #6ee7b7',
            borderRadius: 4,
          }}
        >
          ✓ {done} 表單已重新載入。
        </div>
      )}

      {plan && <PlanView plan={plan} spotId={spotId} onAssigned={afterAssign} />}

      {plan && !plan.applied && (
        <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
          <Button
            variant="primary"
            onClick={() => apply.mutate()}
            disabled={busy || countApplicable(plan) === 0}
          >
            {apply.isPending ? '套用中…' : `套用這 ${countApplicable(plan)} 項`}
          </Button>
          {countApplicable(plan) === 0 && (
            <small style={{ color: '#6b7280' }}>
              沒有可套用的項目
              {!overwrite && '——若要取代既有內容，請勾選上方的覆蓋選項'}
            </small>
          )}
        </div>
      )}
    </div>
  );
}

function countApplicable(plan: SpotFolderPlan): number {
  return (
    plan.media.filter((m) => m.action !== 'skip_existing').length +
    plan.text.filter((t) => t.action !== 'skip_existing').length +
    plan.pois.filter((p) => p.action !== 'skip_existing').length
  );
}

function PlanView({
  plan,
  spotId,
  onAssigned,
}: {
  plan: SpotFolderPlan;
  spotId: string;
  onAssigned: () => void;
}): JSX.Element {
  return (
    <div style={{ marginTop: 10, fontSize: 12 }}>
      {plan.warnings.map((w) => (
        <div
          key={w}
          style={{
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: 4,
            padding: 8,
            marginBottom: 8,
          }}
        >
          ⚠ {w}
        </div>
      ))}

      <PlanSection title="媒體插槽" empty="資料夾裡沒有可對應的圖片 / 語音 / 影片">
        {plan.media.map((m) => (
          <PlanRow
            key={`${m.slot}|${m.locale}`}
            action={m.action}
            label={`${SLOT_LABEL[m.slot] ?? m.slot}（${LOCALE_LABEL[m.locale] ?? m.locale}）`}
            detail={m.files.map((f) => f.filename).join('、')}
            existing={
              m.existing.length > 0
                ? `現有 ${m.existing.length} 個：${m.existing.map((e) => e.filename).join('、')}`
                : null
            }
          />
        ))}
      </PlanSection>

      <PlanSection title="文字欄位" empty="資料夾裡沒有可對應的 str 文字檔">
        {plan.text.map((t) => (
          <PlanRow
            key={`${t.field}|${t.locale}`}
            action={t.action}
            label={`${FIELD_LABEL[t.field] ?? t.field}（${LOCALE_LABEL[t.locale] ?? t.locale}）`}
            detail={`${t.source_filename} → ${truncate(t.value)}`}
            existing={t.existing_value === null ? null : `現有：${truncate(t.existing_value)}`}
          />
        ))}
      </PlanSection>

      <PlanSection title="推薦點" empty="資料夾裡沒有 4_／5_ 子資料夾的內容">
        {plan.pois.map((p) => (
          <PlanRow
            key={`${p.category}|${p.seq}`}
            action={p.action}
            label={`${POI_CATEGORY_SHORT[p.category] ?? p.category} #${p.seq}`}
            // 一格由最多 14 個檔組成，列出檔名會刷掉整個畫面；改列「填了哪些欄位」
            detail={`${describePoiPatch(p.patch)}（${p.sources.length} 個檔）`}
            existing={
              p.existing_name === null
                ? null
                : `現有：${p.existing_name['zh-TW'] ?? Object.values(p.existing_name)[0] ?? '（未命名）'}`
            }
          />
        ))}
      </PlanSection>

      <SpotFolderIssues
        spotId={spotId}
        title="未套用的檔案"
        issues={plan.issues}
        onAssigned={onAssigned}
      />
      {/* 推薦點層維持唯讀：那一層的內容單位是卡片，不是單一檔案對單一格 */}
      <SpotFolderIssues
        spotId={spotId}
        title="推薦點未套用的檔案"
        issues={plan.poi_issues}
        onAssigned={onAssigned}
      />
    </div>
  );
}

/** 由 patch 的鍵組出人看得懂的摘要——欄位名對編輯的人沒有意義。 */
const PATCH_LABEL: Record<string, string> = {
  name_i18n: '店名',
  asr_hint_i18n: '語音提示',
  nearby_i18n: '所在地',
  intro_i18n: '簡介',
  hours_i18n: '營業時間',
  rating: '評分',
  thumb_media_id: '縮圖',
  photo_media_id: '大圖',
  qr_media_id: 'QR',
  keywords_i18n: 'ASR 詞表',
  map_url: '地圖連結',
};

function describePoiPatch(patch: Record<string, unknown>): string {
  const labels = Object.keys(patch)
    .map((k) => PATCH_LABEL[k] ?? k)
    .join('、');
  return labels === '' ? '（無內容）' : labels;
}

function PlanSection({
  title,
  empty,
  children,
}: {
  title: string;
  empty: string;
  children: React.ReactNode;
}): JSX.Element | null {
  const items = Array.isArray(children) ? children : [children];
  const hasContent = items.some((c) => (Array.isArray(c) ? c.length > 0 : Boolean(c)));
  if (!hasContent && empty === '') return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontWeight: 600, marginBottom: 3 }}>{title}</div>
      {hasContent ? children : <div style={{ color: '#9ca3af' }}>{empty}</div>}
    </div>
  );
}

function PlanRow({
  action,
  label,
  detail,
  existing,
}: {
  action: string;
  label: string;
  detail: string;
  existing: string | null;
}): JSX.Element {
  const skipped = action === 'skip_existing';
  const overwriting = action === 'overwrite';
  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        padding: '3px 0',
        borderTop: '1px solid #eef2f7',
        opacity: skipped ? 0.55 : 1,
      }}
    >
      <span
        style={{
          flexShrink: 0,
          width: 52,
          color: skipped ? '#6b7280' : overwriting ? '#b45309' : '#047857',
        }}
      >
        {skipped ? '略過' : overwriting ? '覆蓋' : '填入'}
      </span>
      <span style={{ flexShrink: 0, width: 170 }}>{label}</span>
      <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-all' }}>
        {detail}
        {existing && <div style={{ color: overwriting ? '#b45309' : '#9ca3af' }}>{existing}</div>}
      </span>
    </div>
  );
}

function truncate(s: string): string {
  const oneLine = s.replace(/\n+/g, ' ⏎ ');
  return oneLine.length > 60 ? `${oneLine.slice(0, 60)}…` : oneLine;
}
